import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { aoExpirarSessao, entrar, renovarSessao, sair } from '../api/cliente';
import type { UsuarioSessao } from '../api/tipos';

interface Sessao {
  usuario: UsuarioSessao | null;
  carregando: boolean;
  entrar(email: string, senha: string): Promise<void>;
  sair(): Promise<void>;
  /**
   * Troca o usuário da sessão pelo que o servidor devolveu.
   *
   * <p>É o que faz mudar o nome nas configurações repintar o avatar do topo na hora —
   * todo lugar que desenha iniciais lê daqui. Sem isto, a pessoa só veria o próprio nome
   * novo depois de recarregar a página, e concluiria que não salvou.
   */
  definirUsuario(usuario: UsuarioSessao): void;
}

const Contexto = createContext<Sessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<UsuarioSessao | null>(null);
  const [carregando, setCarregando] = useState(true);

  // No primeiro carregamento, tenta restaurar a sessão pelo cookie de refresh.
  useEffect(() => {
    renovarSessao()
      .then((sessao) => setUsuario(sessao?.usuario ?? null))
      .finally(() => setCarregando(false));
    aoExpirarSessao(() => setUsuario(null));
  }, []);

  const fazerLogin = useCallback(async (email: string, senha: string) => {
    const sessao = await entrar(email, senha);
    setUsuario(sessao.usuario);
  }, []);

  const fazerLogout = useCallback(async () => {
    await sair();
    setUsuario(null);
  }, []);

  const definirUsuario = useCallback((novo: UsuarioSessao) => setUsuario(novo), []);

  const valor = useMemo<Sessao>(
    () => ({ usuario, carregando, entrar: fazerLogin, sair: fazerLogout, definirUsuario }),
    [usuario, carregando, fazerLogin, fazerLogout, definirUsuario]
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): Sessao {
  const sessao = useContext(Contexto);
  if (!sessao) throw new Error('useSessao precisa estar dentro de <ProvedorSessao>');
  return sessao;
}
